import {
  Box,
  CircularProgress,
  Container,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderSignOut from "../../../components/headers/HeaderSignOut";
import { validationMessage } from "../../../consts/ValidationMessage";
import { FormDataType, FormErrorsType } from "../../../types/PlanUpgradePage";
import { encodePrivateKey } from "../../../utils/fileUtils";
import {
  validateRequiredFields,
} from "../../../utils/validationUtils";
import VonageInfoFormStep from "./0_VonageInfoFormStep";
import { API_URL } from "../../../consts/ApiUrl";

const PlanUpgradeStepperPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormDataType>({
    vonageInfo: {
      applicationId: "",
      apiKey: "",
      apiSecret: "",
      webhookUrl: "",
      file: null,
    },
  });

  const [formErrors, setFormErrors] = useState<FormErrorsType>(
    { vonageInfo: {} }
  );

  const fetchWithJson = async (): Promise<boolean> => {
    let encodedPrivateKey = "";
    try {
      encodedPrivateKey = await encodePrivateKey(formData.vonageInfo.file);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      navigate("/menu");
      return false;
    }
    const dataToSend = {
      operation: "upgrade",
      applicationId: formData.vonageInfo.applicationId,
      apiKey: formData.vonageInfo.apiKey,
      apiSecret: formData.vonageInfo.apiSecret,
      incomingWebhookUrl: formData.vonageInfo.webhookUrl,
      privateKey: encodedPrivateKey,
    };
    const token: string = sessionStorage.getItem("idToken") || "";
    const response: Response = await fetch(API_URL.USER, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authentication: token,
      },
      body: JSON.stringify(dataToSend),
    });
    if (!response.ok) {
      if (response.status === 401) {
        navigate("/signin");
      } else {
        navigate("/error");
      }
      return false;
    }
    return true;
  };

  const handleNextStep: () => Promise<void> = async (): Promise<void> => {
    if (validateStep()) {
      try {
        setIsLoading(true);
        if (activeStep === 0) {
          const dataToSend = {
            api_key: formData.vonageInfo.apiKey,
          };
          const token: string = sessionStorage.getItem("idToken") || "";
          const response: Response = await fetch(API_URL.UNICON, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authentication: token,
            },
            body: JSON.stringify(dataToSend),
          });
          if (!response.ok) {
            navigate("/error");
          }
          const data = await response.json();
          const result = data.result;
          if (result.status !== "updated") {
            const message = `
            ${result.message1}
            <a href="${result.link}" target="_blank" rel="noopener noreferrer">こちら</a>
            ${result.message2}
          `;
            setFormErrors((prev) => ({
              ...prev,
              vonageInfo: {
                apiKey: message,
              },
            }));
            setIsLoading(false);
            return;
          }
          const saved = await fetchWithJson();
          setIsLoading(false);
          if (saved) navigate("/upgrade/comp");
          return;
        }
        setActiveStep((prev: number) => prev + 1);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        navigate("/error");
      }
    }
  };

  const validateStep: () => boolean = (): boolean => {
    const required = validationMessage.REQUIRED;

    const errors: Partial<FormErrorsType> = {};

    if (activeStep === 0) {
      const { applicationId, apiKey, apiSecret, webhookUrl, file } =
        formData.vonageInfo;
      const vonageRequiredErrors = validateRequiredFields(
        { applicationId, apiKey, apiSecret, webhookUrl },
        ["applicationId", "apiKey", "apiSecret"],
        {
          applicationId: `アプリケーションID${required}`,
          apiKey: `APIキー${required}`,
          apiSecret: `APIシークレット${required}`,
        }
      );

      if (!vonageRequiredErrors.applicationId) {
        if (
          !/^[a-zA-Z0-9-]{36}$/.test(applicationId) ||
          (applicationId.match(/-/g) || []).length !== 4
        ) {
          vonageRequiredErrors.applicationId = validationMessage.APP_ID_FORMAT;
        }
      }
      if (!vonageRequiredErrors.apiKey) {
        if (!/^[a-zA-Z0-9]{8}$/.test(apiKey)) {
          vonageRequiredErrors.apiKey = validationMessage.API_KEY_FORMAT;
        }
      }
      if (!vonageRequiredErrors.apiSecret) {
        // 文字数チェック（8文字から25文字）
        if (apiSecret.length < 8 || apiSecret.length > 25) {
          vonageRequiredErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
        }
        // 大文字が1つ以上含まれているかチェック
        else if (!/[A-Z]/.test(apiSecret)) {
          vonageRequiredErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
        }
        // 小文字が1つ以上含まれているかチェック
        else if (!/[a-z]/.test(apiSecret)) {
          vonageRequiredErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
        }
        // 数字が1つ以上含まれているかチェック
        else if (!/[0-9]/.test(apiSecret)) {
          vonageRequiredErrors.apiSecret = validationMessage.API_SECRET_FORMAT;
        }
      }
      if (!file) {
        vonageRequiredErrors.file = `秘密鍵${required}`;
      }
      if (!vonageRequiredErrors.file) {
        if (file && !file.name.endsWith(".key")) {
          vonageRequiredErrors.file = validationMessage.SECRET_KEY_FORMAT;
        }
      }
      errors.vonageInfo = vonageRequiredErrors;
    }

    setFormErrors((prev) => ({
      ...prev,
      ...errors,
    }));

    const hasErrors = Object.values(errors).some((error) => {
      return (
        error &&
        Object.keys(error).some((key) => {
          const value: string | undefined = error[key as keyof typeof error];
          return value && value.length > 0;
        })
      );
    });

    return !hasErrors;
  };

  const handleBack: () => void = (): void => {
    if (activeStep > 0) setActiveStep((prev: number): number => prev - 1);
    else navigate("/menu");
  };

  const updateFormData = <T extends keyof FormDataType>(
    step: T,
    updatedData: Partial<FormDataType[T]>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [step]: { ...prev[step], ...updatedData },
    }));
    setFormErrors((prev) => ({ ...prev, [step]: {} }));
  };

  const steps = [
    <VonageInfoFormStep
      data={formData.vonageInfo}
      errors={formErrors.vonageInfo}
      onChange={(e) =>
        updateFormData("vonageInfo", { [e.target.name]: e.target.value })
      }
      onFileChange={(file) => updateFormData("vonageInfo", { file })}
      onNext={handleNextStep}
      onBack={handleBack}
    />,
  ];

  return (
    <>
      <HeaderSignOut />
      <Container maxWidth="sm">
        <Box mt={5}>
          <Box mt={4}>
            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="200px"
              >
                <CircularProgress />
              </Box>
            ) : (
              steps[activeStep]
            )}
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default PlanUpgradeStepperPage;
